import { Button } from "@community-project-org/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bus, Clapperboard, FlaskConical, Key, Music, Stethoscope, Trash2 } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { apiRequest, ApiRequestError } from "../api/apiClient.js";
import type { components } from "../../generated/api.js";
import { useActiveMembership } from "./ActiveMembership.js";
import { ROLE_LABELS, type TenantRole } from "./roles.js";

type TestDataTemplate = components["schemas"]["TestDataTemplate"];
type TestTenantDTO = components["schemas"]["TestTenantDTO"];

const TEMPLATES: { template: TestDataTemplate; label: string; icon: typeof Clapperboard }[] = [
  { template: "CINEMA", label: "Kino", icon: Clapperboard },
  { template: "FESTIVAL", label: "Festival", icon: Music },
  { template: "PRACTICE", label: "Praxis", icon: Stethoscope },
  { template: "RENTAL", label: "Verleih", icon: Key },
  { template: "BUS", label: "Fernbus", icon: Bus },
];

/**
 * Werkzeuge für interne Admins im Konto-Menü: in jedem Mandanten jede Rolle einnehmen
 * und Testmandanten nach Vorlage anlegen oder entfernen.
 *
 * Erscheint nur, wenn /me einen `internal`-Block liefert — also auf Testsystemen für
 * die Gruppe `internal`. Durchgesetzt wird das auf dem Server; hier ist es nur die
 * Abkürzung.
 */
export function InternalTools({ onDone }: { onDone: () => void }): JSX.Element | null {
  const { me, active, assumed, assume, setActive } = useActiveMembership();
  const queryClient = useQueryClient();
  const internal = me?.internal;

  const [tenantId, setTenantId] = useState(active?.tenantId ?? internal?.tenants[0]?.tenantId ?? "");
  const [role, setRole] = useState<TenantRole>(active?.role ?? "TENANT_ADMIN");

  const create = useMutation({
    mutationFn: (template: TestDataTemplate) =>
      apiRequest<TestTenantDTO>("/internal/test-data", {
        method: "POST",
        body: { template },
        tenant: null,
      }),
    onSuccess: async (created) => {
      // Erst die neue Mandantenliste abwarten, sonst fände die Wahl ihn noch nicht.
      await queryClient.refetchQueries({ queryKey: ["me"] });
      assume(created.tenantId, "TENANT_ADMIN");
      onDone();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/internal/test-data/${encodeURIComponent(id)}`, {
        method: "DELETE",
        tenant: null,
      }),
    onSuccess: async () => {
      setActive(null);
      await queryClient.refetchQueries({ queryKey: ["me"] });
      onDone();
    },
  });

  if (!internal) {
    return null;
  }

  const activeTenant = internal.tenants.find((entry) => entry.tenantId === active?.tenantId);
  const fehler = create.error ?? remove.error;

  return (
    <div className="border-t border-line px-4 pb-3">
      <p className="micro flex items-center gap-1.5 pb-1 pt-3">
        <FlaskConical size={13} aria-hidden="true" />
        Intern
      </p>

      {internal.tenants.length > 0 && (
        <form
          className="grid gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (tenantId) {
              assume(tenantId, role);
              onDone();
            }
          }}
        >
          <label className="grid gap-1 text-[12px] font-bold text-ink-2">
            Unternehmen
            <select
              className="min-h-[44px] rounded-md border border-line bg-surface px-2 text-[13.5px] text-ink"
              value={tenantId}
              onChange={(event) => setTenantId(event.target.value)}
            >
              {internal.tenants.map((tenant) => (
                <option key={tenant.tenantId} value={tenant.tenantId}>
                  {tenant.isTestData ? `${tenant.tenantName} (Testdaten)` : tenant.tenantName}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-[12px] font-bold text-ink-2">
            Rolle
            <select
              className="min-h-[44px] rounded-md border border-line bg-surface px-2 text-[13.5px] text-ink"
              value={role}
              onChange={(event) => setRole(event.target.value as TenantRole)}
            >
              {internal.roles.map((entry) => (
                <option key={entry.role} value={entry.role}>
                  {ROLE_LABELS[entry.role].label}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit">Rolle einnehmen</Button>
        </form>
      )}

      <p className="pb-1 pt-3 text-[12px] font-bold text-ink-2">Testdaten erzeugen</p>
      <div className="grid grid-cols-2 gap-2">
        {TEMPLATES.map(({ template, label, icon: Icon }) => (
          <Button
            key={template}
            variant="secondary"
            disabled={create.isPending}
            aria-busy={create.isPending && create.variables === template}
            onClick={() => create.mutate(template)}
          >
            <Icon size={15} aria-hidden="true" />
            {create.isPending && create.variables === template ? "…" : label}
          </Button>
        ))}
      </div>

      {assumed && activeTenant?.isTestData && (
        <Button
          variant="danger"
          className="mt-2 w-full"
          disabled={remove.isPending}
          onClick={() => {
            if (window.confirm(`„${activeTenant.tenantName}" mit allen Buchungen löschen?`)) {
              remove.mutate(activeTenant.tenantId);
            }
          }}
        >
          <Trash2 size={15} aria-hidden="true" />
          {remove.isPending ? "Wird gelöscht …" : "Diesen Testmandanten löschen"}
        </Button>
      )}

      {fehler && (
        <p className="pt-2 text-[12px] text-danger" role="alert">
          {fehler instanceof ApiRequestError ? fehler.message : "Das hat nicht geklappt."}
        </p>
      )}
    </div>
  );
}
